'use client'

import { useCallback, useEffect, useState } from 'react'
import { fetchAppFreeVhfProgress } from '@/lib/app-data/free-vhf-progress'
import { useAuth } from '@/lib/auth/auth-context'
import {
  FREE_VHF_SCENARIO_IDS_CLIENT,
  FREE_VHF_TOTAL_LESSONS_CLIENT,
} from '@/lib/free-vhf-scenario-ids'
import { useFreeVhfProgressContext } from '@/lib/free-vhf-progress-context'

export interface FreeVhfProgressState {
  completedScenarioIds: string[]
  completedCount: number
  totalLessons: number
  percent: number
  nextScenarioId: string | null
  loading: boolean
  error: string | null
  mutate: () => void
}

/**
 * Fetches free VHF progress (free_vhf_progress) for the current user.
 * When inside FreeVhfProgressProvider (free shell), uses shared state so mutate() updates sidebar.
 * Used in free VHF sidebar, My learning tab, and FreeVhfLessonProgressRecorder.
 */
export function useFreeVhfProgress(): FreeVhfProgressState {
  const context = useFreeVhfProgressContext()
  const { user } = useAuth()
  const [completedScenarioIds, setCompletedScenarioIds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchProgress = useCallback(() => {
    if (!user) {
      setCompletedScenarioIds([])
      setLoading(false)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)

    fetchAppFreeVhfProgress()
      .then((rows) => {
        setCompletedScenarioIds(rows.map((r) => r.scenario_id))
        setLoading(false)
      })
      .catch(() => {
        setCompletedScenarioIds([])
        setLoading(false)
        setError('Failed to load progress')
      })
  }, [user])

  useEffect(() => {
    const t = setTimeout(() => fetchProgress(), 0)
    return () => clearTimeout(t)
  }, [fetchProgress])

  const completedSet = new Set(completedScenarioIds)
  const completedCount = completedScenarioIds.length
  const totalLessons = FREE_VHF_TOTAL_LESSONS_CLIENT
  const percent = totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0
  const nextScenarioId =
    FREE_VHF_SCENARIO_IDS_CLIENT.find((id) => !completedSet.has(id)) ?? null

  if (context) return context

  return {
    completedScenarioIds,
    completedCount,
    totalLessons,
    percent,
    nextScenarioId,
    loading,
    error,
    mutate: fetchProgress,
  }
}
